import React from "react";
import "./button.scss";
import { NavLink } from "react-router-dom";
import useVariant, { VariantTypes } from "src/hooks/useVariant";
import ReactTooltip from "react-tooltip";
import Indicator from "src/components/layout/Indicator";
import { lightenDarkenColor } from "src/lib/colors";
import useElementSize from "src/hooks/useElementSize";
import Loader from "src/components/Loader";
import { useLocation } from "react-router-dom";

type To = {
	modal: string;
	back?: {
		to: string;
	};
};

export type ButtonProps = {
	type: string;
	onClick?: (e?: any) => void;
	children?: any;
	loading?: boolean;
	disabled?: boolean;
	to?: string | To;
	submit?: boolean;
	small?: boolean;
	icon?: string;
	href?: string;
	className?: string;
	noBlank?: boolean;
	style?: any;
	target?: string;
	help?: string;
	id?: string;
	iconOnly?: boolean;
	download?: boolean;
	indicator?: any;
	tabIndex?: string | number;
	gtm?: string;
};

const Button = ({
	type,
	onClick,
	children,
	loading = true,
	to,
	disabled,
	submit,
	small,
	icon,
	href,
	className,
	noBlank,
	style,
	target,
	help,
	id,
	iconOnly,
	download,
	indicator,
	tabIndex,
	gtm,
}: ButtonProps) => {
	const location = useLocation();
	const classes = [
		"button rounded-md transition-all text-center justify-center flex items-center relative whitespace-nowrap",
		`button_${type === "orange" ? "primary" : type}`,
	];
	if (typeof to === "string" && to?.startsWith("http")) {
		href = to;
		to = undefined;
	}

	const variant = useVariant((type === "orange" ? "primary" : type) as any);
	const { ref, size } = useElementSize();

	if (type) {
		classes.push(variant);
	}
	if (loading) classes.push("opacity-90");
	if (disabled) classes.push("opacity-50 button_disabled cursor-not-allowed");
	if (small) classes.push("button_small");
	if (icon) classes.push("button_icon");
	if (className) classes.push(className);

	if (iconOnly) {
		if (!classes.join(" ")?.includes(" w-")) {
			classes.push("p-2 w-[40px]");
			classes.push(`h-[${size.width}px]`);
		}
	} else if (!className?.includes("p-")) {
		if (small) {
			classes.push("py-2 px-4 text-[16px]");
		} else {
			classes.push("py-2.5 px-5");
		}
	}

	if (gtm) classes.push(gtm);

	const renderChildren = () => {
		return (
			<>
				{loading ? (
					<div className="absolute inset-0 backdrop-blur-sm flex justify-center items-center rounded-md">
						<Loader />
					</div>
				) : (
					<></>
				)}

				{icon ? (
					<i className={icon} style={{ pointerEvents: "none" }}></i>
				) : (
					children
				)}

				{indicator ? (
					<>
						{iconOnly ? (
							<Indicator className="absolute -top-2 -right-2">
								{indicator}
							</Indicator>
						) : (
							<Indicator className="absolute top-1 right-1">
								{indicator}
							</Indicator>
						)}
					</>
				) : (
					false
				)}
			</>
		);
	};

	if (to) {
		return (
			<>
				{help && (
					<ReactTooltip id={`tooltip-${id}`} effect="solid">
						{help}
					</ReactTooltip>
				)}
				<NavLink
					data-tip
					data-for={`tooltip-${id}`}
					to={typeof to === "string" ? to : to?.modal}
					state={
						typeof to !== "string" && to?.modal
							? {
									background: location,
									back: to?.back,
							  }
							: undefined
					}
					{...{ style, target, ref }}
					onClick={(e) => {
						if (disabled || loading) {
							e.preventDefault();
						}
					}}
					className={classes.join(" ")}
				>
					{renderChildren()}
				</NavLink>
			</>
		);
	}

	if (href) {
		return (
			<>
				{help && (
					<ReactTooltip id={`tooltip-${id}`} effect="solid">
						{help}
					</ReactTooltip>
				)}
				<a
					data-tip
					data-for={`tooltip-${id}`}
					{...{ href, style, download, ref }}
					target={!noBlank ? "_blank" : undefined}
					rel="noopener noreferrer"
					className={classes.join(" ")}
				>
					{renderChildren()}
				</a>
			</>
		);
	}

	return (
		<>
			{help && (
				<ReactTooltip id={`tooltip-${id}`} effect="solid">
					{help}
				</ReactTooltip>
			)}
			<button
				data-tip
				data-for={`tooltip-${id}`}
				type={submit ? "submit" : "button"}
				{...{ style, ref }}
				className={classes.join(" ")}
				onClick={(e) => {
					if (!disabled && onClick && !loading) {
						onClick(e);
					}
				}}
				tabIndex={tabIndex ? Number(tabIndex) : undefined}
			>
				{renderChildren()}
			</button>
		</>
	);
};

Button.defaultProps = {
	type: "primary",
	onClick: () => {},
	loading: false,
};

interface IconButtonProps {
	onClick?: () => void;
	variant: VariantTypes;
	icon: string;
	loading: boolean;
	customColor?: string;
}

const IconButton = ({
	onClick,
	variant,
	icon,
	loading,
	customColor,
}: IconButtonProps) => {
	const classes = ["text-[16px] w-8 h-8 rounded-md"];
	const variantClassName = useVariant(variant);
	const style = {} as any;

	if (!customColor) {
		classes.push(variantClassName);
	}

	if (customColor) {
		style.color = customColor;
		style.backgroundColor = lightenDarkenColor(customColor, 99);
	}

	return (
		<button className={classes.join(" ")} {...{ onClick, style }}>
			{loading ? (
				<span className="base-loader"></span>
			) : (
				<i className={icon}></i>
			)}
		</button>
	);
};

IconButton.defaultProps = {
	variant: "gray",
	loading: false,
};

Button.Icon = IconButton;

export default Button;
